import { Check, Copy, Mail } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  useGetDrtIntegrationsByStudio,
  useSendTicketsMutation,
} from '~entities/drt-integration';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { ScrollArea } from '~shared/shadcn/ui/scroll-area';

interface SendTicketsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedParents: Array<{ _id: string; name: string; email: string }>;
}

// DRT Buy Tickets link format per DRT API docs
const DRT_INTEGRATOR_BASE = 'https://integrator.drttix.com';
const DRT_SLUG = import.meta.env.VITE_DRT_INTEGRATOR_SLUG ?? 'enrollio';

const buildBuyTicketsUrl = (
  accountId: string,
  customerId?: string,
): string => {
  const path = [DRT_SLUG, accountId];
  if (customerId) {
    path.push(customerId);
  }
  return `${DRT_INTEGRATOR_BASE}/${path.join('/')}`;
};

export const SendTicketsDialog = ({
  open,
  onOpenChange,
  selectedParents,
}: SendTicketsDialogProps) => {
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const { data: studioDetails } = useGetStudioDetails();
  const { data: integrations = [] } = useGetDrtIntegrationsByStudio(
    studioDetails?._id ?? '',
  );

  const activeIntegration = integrations.find((i) => i.isActive);
  const accountId = activeIntegration?.accountId;

  const { mutate: sendTickets, isPending: isSending } =
    useSendTicketsMutation();

  const copyToClipboard = async (
    text: string,
    fallbackMessage?: string,
  ): Promise<boolean> => {
    // Try modern clipboard API first
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      console.log('Modern clipboard API failed:', error);
    }

    // Fallback 1: execCommand method
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      textArea.remove();
      if (success) return true;
    } catch (error) {
      console.log('execCommand failed:', error);
    }

    // Fallback 2: Try to select the input field
    try {
      const inputElement = document.querySelector(
        `input[value="${text}"]`,
      ) as HTMLInputElement;

      if (inputElement) {
        inputElement.focus();
        inputElement.select();
        if (fallbackMessage) {
          toast.info(fallbackMessage, { duration: 4000 });
        }
        return true;
      }
    } catch (error) {
      console.error('Selection method failed:', error);
    }

    return false;
  };

  const handleCopyUrl = async (url: string, parentName: string) => {
    const success = await copyToClipboard(
      url,
      `Text selected! Press Ctrl+C (or Cmd+C) to copy ${parentName}'s link`,
    );
    if (success) {
      setCopiedUrl(url);
      toast.success(`Ticket link for ${parentName} copied!`);
      setTimeout(() => {
        setCopiedUrl(null);
      }, 2000);
    } else {
      toast.error(
        'Auto-copy not available. Please manually select and copy the text.',
      );
    }
  };

  const handleCopyAllLinks = async () => {
    if (!accountId) {
      toast.error('Account ID not available');
      return;
    }

    const allLinks = selectedParents
      .map(
        (parent) =>
          `${parent.name} (${parent.email}): ${buildBuyTicketsUrl(accountId, parent._id)}`,
      )
      .join('\n');

    const success = await copyToClipboard(
      allLinks,
      'Text selected! Press Ctrl+C (or Cmd+C) to copy all links',
    );
    if (success) {
      toast.success('All ticket links copied to clipboard!');
    } else {
      toast.error(
        'Auto-copy not available. Please manually select and copy the text.',
      );
    }
  };

  const handleSendEmails = () => {
    if (!activeIntegration) {
      toast.error('DRT integration not found');
      return;
    }

    const parentIds = selectedParents.map((parent) => parent._id);

    sendTickets(
      {
        parentIds,
        integrationId: activeIntegration._id,
      },
      {
        onSuccess: (response) => {
          if (response.failed === 0) {
            toast.success(
              `Successfully sent ticket links to ${response.sent} ${response.sent === 1 ? 'family' : 'families'}`,
            );
          } else if (response.sent === 0) {
            toast.error('Failed to send ticket links to all families');
          } else {
            toast.warning(
              `Sent to ${response.sent} ${response.sent === 1 ? 'family' : 'families'}, failed for ${response.failed}`,
            );
          }

          if (response.errors && response.errors.length > 0) {
            console.warn('Errors sending tickets:', response.errors);
          }
        },
        onError: (error) => {
          console.error('Failed to send ticket emails:', error);
          toast.error('Failed to send ticket links via email');
        },
      },
    );
  };

  if (!activeIntegration) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>DRT Integration Required</DialogTitle>
            <DialogDescription>
              You need to set up DRT integration first to generate ticket
              links. Please go to the DRT Integration page to configure your
              account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant='outline'>Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (!accountId) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Account ID Not Available</DialogTitle>
            <DialogDescription>
              Unable to generate ticket links. Account ID is missing from studio
              details.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant='outline'>Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Send Ticket Links to Families</DialogTitle>
          <DialogDescription>
            Copy personalized ticket links for {selectedParents.length} selected{' '}
            {selectedParents.length === 1 ? 'family' : 'families'}. Each link
            is pre-authenticated with family details.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <Label>Personalized Ticket Links</Label>
            <Button
              variant='outline'
              size='sm'
              onClick={() => void handleCopyAllLinks()}
            >
              <Copy className='mr-2 size-4' />
              Copy All Links
            </Button>
          </div>
          <ScrollArea className='h-[300px] rounded-md border p-4'>
            <div className='space-y-3'>
              {selectedParents.map((parent) => {
                const ticketUrl = buildBuyTicketsUrl(accountId, parent._id);
                return (
                  <div
                    key={parent._id}
                    className='space-y-2 rounded-lg border p-3'
                  >
                    <div className='flex items-center justify-between'>
                      <div>
                        <p className='font-medium capitalize'>{parent.name}</p>
                        <p className='text-sm text-muted-foreground'>
                          {parent.email}
                        </p>
                      </div>
                    </div>
                    <div className='flex gap-2'>
                      <Input
                        value={ticketUrl}
                        readOnly
                        className='font-mono text-xs'
                      />
                      <Button
                        variant='outline'
                        size='icon'
                        onClick={() =>
                          void handleCopyUrl(ticketUrl, parent.name)
                        }
                      >
                        {copiedUrl === ticketUrl ? (
                          <Check className='size-4' />
                        ) : (
                          <Copy className='size-4' />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <div className='rounded-md bg-muted p-3 text-sm text-muted-foreground'>
            <p className='font-medium'>How to use:</p>
            <ol className='mt-2 list-inside list-decimal space-y-1'>
              <li>Copy individual links or all links at once</li>
              <li>
                Paste the links into your email platform (e.g., GoHighLevel,
                Mailchimp)
              </li>
              <li>
                When families click their link, their details, coupons, and
                performer info will load automatically
              </li>
            </ol>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline' disabled={isSending}>
              Close
            </Button>
          </DialogClose>
          <Button onClick={handleSendEmails} disabled={isSending}>
            {isSending && <Mail className='mr-2 size-4 animate-spin' />}
            {isSending ? 'Sending...' : 'Send Email Links'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
